'use client';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Long enough for the hit to leave, short enough not to stall a redirect. */
const CALLBACK_TIMEOUT_MS = 1000;

interface Args {
  /** `AW-XXXXXXXXXX/label` - the account ID and the conversion action. */
  sendTo: string;
  /** Google Ads drops repeats of the same id, e.g. a double submit. */
  transactionId: string;
  /** Enhanced conversions - gtag normalises and hashes it before sending. */
  email?: string;
  value?: number;
  currency?: string;
}

/**
 * Resolves once gtag reports the hit as sent, or after a timeout - await it
 * before navigating away, a redirect would otherwise cut the request short.
 */
export function fireAdsConversion({
  sendTo,
  transactionId,
  email,
  value,
  currency,
}: Args): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  const { gtag } = window;
  if (typeof gtag !== 'function') return Promise.resolve();

  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, CALLBACK_TIMEOUT_MS);
    const done = () => {
      clearTimeout(timeout);
      resolve();
    };

    try {
      if (email) gtag('set', 'user_data', { email });
      gtag('event', 'conversion', {
        send_to: sendTo,
        transaction_id: transactionId,
        ...(value !== undefined && { value, currency }),
        event_callback: done,
      });
    } catch (err) {
      console.error('[Google Ads] Failed to fire conversion:', err);
      done();
    }
  });
}
